import type { QueryKey } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { getAuthHeader } from "../../../tokenStorage";
import type { UseGetOptions, UseGetResult } from "../models";
import { useReactQueryWrappersProvider } from "./useReactQueryWrappersProvider";

export const useGet = <
  TData = unknown,
  TError = unknown,
  TParams = Record<string, unknown>,
>({
  url,
  params,
  axiosConfig = {},
  ...queryOptions
}: UseGetOptions<TData, TError, TParams>): UseGetResult<TData, TError> => {
  if (typeof url !== "string" || !url.trim()) {
    throw new Error("useGet requires a non-empty URL.");
  }

  const { axios } = useReactQueryWrappersProvider();

  const queryResult = useQuery<
    AxiosResponse<TData>,
    TError,
    AxiosResponse<TData>,
    QueryKey
  >({
    queryKey: params ? [url, params] : [url],
    queryFn: () =>
      axios.get<TData>(url, {
        ...axiosConfig,
        params,
        headers: {
          ...getAuthHeader(),
          ...axiosConfig.headers,
        },
      }),
    ...queryOptions,
  });

  return {
    ...queryResult,
    data: queryResult.data?.data,
  };
};
